import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = ({ selectedService, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: selectedService || '',
    budget: '',
    timeline: '',
    message: '',
    projectType: ''
  });

  const [errors, setErrors] = useState({});

  const serviceOptions = [
    { value: 'web-development', label: '3D Web Development' },
    { value: 'portfolio-design', label: 'Portfolio Design' },
    { value: 'brand-identity', label: 'Brand Identity' },
    { value: 'ui-ux-design', label: 'UI/UX Design' },
    { value: 'creative-direction', label: 'Creative Direction' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'workshop', label: 'Workshop/Speaking' },
    { value: 'collaboration', label: 'Collaboration' }
  ];

  const budgetOptions = [
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k+', label: '$100,000+' },
    { value: 'discuss', label: 'Let\'s Discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: '1-month', label: '1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6-months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const projectTypeOptions = [
    { value: 'new-project', label: 'New Project' },
    { value: 'redesign', label: 'Redesign/Refresh' },
    { value: 'maintenance', label: 'Maintenance/Updates' },
    { value: 'consultation', label: 'Consultation Only' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Email is invalid';
    if (!formData?.service) newErrors.service = 'Please select a service';
    if (!formData?.message?.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel rounded-2xl p-8 max-w-2xl mx-auto"
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-accent to-cosmic-energy rounded-xl flex items-center justify-center cosmic-glow">
          <Icon name="MessageSquare" size={24} className="text-background" />
        </div>
        <div>
          <h3 className="font-headline text-xl text-foreground">Project Inquiry</h3>
          <p className="font-body text-sm text-muted-foreground">Let's create something amazing together</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            type="text"
            placeholder="Your name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Company/Organization"
            type="text"
            placeholder="Your company (optional)"
            value={formData?.company}
            onChange={(e) => handleInputChange('company', e?.target?.value)}
          />

          <Select
            label="Service Needed"
            placeholder="Select a service"
            options={serviceOptions}
            value={formData?.service}
            onChange={(value) => handleInputChange('service', value)}
            error={errors?.service}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Project Type"
            placeholder="Select project type"
            options={projectTypeOptions}
            value={formData?.projectType}
            onChange={(value) => handleInputChange('projectType', value)}
          />

          <Select
            label="Budget Range"
            placeholder="Select budget range"
            options={budgetOptions}
            value={formData?.budget}
            onChange={(value) => handleInputChange('budget', value)}
          />
        </div>

        <Select
          label="Timeline"
          placeholder="When do you need this completed?"
          options={timelineOptions}
          value={formData?.timeline}
          onChange={(value) => handleInputChange('timeline', value)}
        />

        <div>
          <label className="block font-body font-medium text-foreground mb-2">
            Project Details *
          </label>
          <textarea
            className="w-full h-32 px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
            placeholder="Tell me about your project, goals, and any specific requirements..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            required
          />
          {errors?.message && (
            <p className="mt-1 text-sm text-destructive">{errors?.message}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
            className="flex-1"
          >
            {isSubmitting ? 'Sending Message...' : 'Send Message'}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            iconName="Calendar"
            iconPosition="left"
            onClick={() => window.open('https://calendly.com/portfolio-consultation', '_blank')}
          >
            Schedule Call
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default ContactForm;